import type { SampleDetail } from '../sample_detail/types';
import type { Variant } from '../variant_check/redux/types';
import type {
  ArgumentForClassification,
  ClinVarAmbiguousResponse,
  ClinvarAssertion,
  ClinVarGeneDistribution,
  ClinVarHotSpot,
  ClinVarHotSpotVariantList,
  ClinvarRecordType,
  Gene,
  GeneFactsOverview,
  InterpretationSnapshot,
  InterpretationSnapshotCreateRequest,
  InterpretationSnapshotListItem,
  InterpretationZone,
  MaxAFScoring,
  MolecularImpactHeaderType,
  OtherVariantsResponse,
  ProteinDomain,
  Reference,
  RefreshAndRecalculateResponse,
  RunningTask,
  SilicoPredictionType,
  UpdateGeneRequest,
  VariantAFPopulationData,
  VariantAssessmentStage,
  VariantAssessmentStageUpdateResponse,
  VariantCommentType,
  VariantDatabase,
  VariantDatabaseUpdateRequest,
  VariantHistory,
  VariantLinkouts,
  VariantLock,
  VariantSample,
  VariantTranscript,
} from '../variant_workflow/redux';
import type { WorklistSample } from '../worklist/types';
import { apiSlice } from './apiSlice';
import type { AxiosBaseQueryError } from './axiosBaseQuery';

// Import the single API slice object so we can inject endpoints into it
// (see RTK Query docs: "Code Splitting and Generation" https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting).

// RTK Query endpoints for variant-specific data.
//
// Endpoints are injected into the single API slice via injectEndpoints().
// This keeps one API slice per application while allowing code-splitting
// of endpoint definitions across feature files.
//
// Tag invalidation relationships:
// - Query endpoints use providesTags with { type, id } for fine-grained cache control
// - Mutation endpoints use invalidatesTags with { type, id } to trigger refetches

export interface VCFUploadResponse {
  batch_id: string;
  queued_count: number;
  failed_count: number;
  skipped_lines: number;
  message: string;
}

// Core Variant Data

export const variantApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getRunningTasks: builder.query<RunningTask[], void>({
      query: () => ({ url: '/running-tasks' }),
      providesTags: ['RunningTasks'],
      merge(currentTasks, incomingTasks) {
        const incomingTaskIds = new Set(
          incomingTasks.map((task) => task.task_id),
        );
        const optimisticTasks = currentTasks.filter(
          (task) => task.is_optimistic && !incomingTaskIds.has(task.task_id),
        );
        currentTasks.splice(
          0,
          currentTasks.length,
          ...optimisticTasks,
          ...incomingTasks,
        );
      },
    }),
    uploadVcf: builder.mutation<VCFUploadResponse, FormData>({
      query: (data) => ({
        url: '/resultdata/upload-vcf',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['RunningTasks'],
    }),
    // Variant detail (summary, clinical observation, etc.)
    // BIOFRO-80 ABS review: replace any with proper type
    getVariantDetail: builder.query<Variant, { variant_id?: number | string }>({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/detail/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'VariantDetail', id: variant_id },
      ],
    }),

    // Variant comments
    // BIOFRO-80 ABS review: replace any with proper type
    getVariantComments: builder.query<
      VariantCommentType[],
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/comments/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'VariantComments', id: variant_id },
      ],
    }),

    // Variant history (changelog)
    // BIOFRO-80 ABS review: replace any with proper type
    getVariantHistory: builder.query<
      VariantHistory,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/history/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'VariantHistory', id: variant_id },
      ],
    }),

    // Variant-assessment stages (workflow stages)
    getVariantAssessmentStages: builder.query<
      VariantAssessmentStage[],
      { variant_id?: number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/variantassessmentstages/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'VariantStages', id: variant_id },
      ],
    }),

    // Interpretation zone data
    // BIOFRO-80 ABS review: replace any with proper type
    getVariantInterpretationZone: builder.query<
      InterpretationZone,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/interpretation_zone/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'InterpretationZone', id: variant_id },
      ],
    }),

    // Variant-assessment arguments for classification table
    getVariantAssessmentArguments: builder.query<
      ArgumentForClassification[],
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/variant_assessment_arguments_table_data/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'VariantArguments', id: variant_id },
      ],
    }),

    getInterpretationSnapshots: builder.query<
      InterpretationSnapshotListItem[],
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/${variant_id}/interpretation-snapshots`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'InterpretationSnapshots', id: variant_id },
      ],
    }),

    getInterpretationSnapshot: builder.query<
      InterpretationSnapshot,
      { variant_id?: string | number; snapshot_id: number }
    >({
      query: ({ variant_id, snapshot_id }) => ({
        url: `/variantdata/variant/${variant_id}/interpretation-snapshots/${snapshot_id}`,
      }),
      providesTags: (result, error, { snapshot_id }) => [
        { type: 'InterpretationSnapshots', id: snapshot_id },
      ],
    }),

    // ─── Frequency Data ─────────────────────────────────────────────────────

    getVariantFrequencies: builder.query<
      VariantAFPopulationData,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/frequencies/${variant_id}`,
      }),
      // The backend returns a raw array on GET but an object { frequencies, versioning_info }
      // on the update endpoint. We normalise both shapes to the expected
      // VariantAFPopulationData structure so components can always read .frequencies safely.
      transformResponse: (response: any) => {
        if (Array.isArray(response)) {
          return {
            frequencies: response,
            versioning_info: { source_version: undefined, date: undefined },
          };
        }
        return response;
      },
      providesTags: (result, error, { variant_id }) => [
        { type: 'VariantFrequencies', id: variant_id },
      ],
    }),

    getGeneTopPatVarFrequencies: builder.query<any, { gene_id?: number }>({
      query: ({ gene_id }) => ({
        url: `/variantdata/variant/genes_top_pat_var_frequencies/${gene_id}`,
      }),
      providesTags: (result, error, { gene_id }) => [
        { type: 'GeneTopPatVarFrequencies', id: gene_id },
      ],
    }),

    getMaxAFScorings: builder.query<
      MaxAFScoring[],
      { variant_id?: number | string }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/maxcafscorings/${variant_id}?langcode=en`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'MaxAFScorings', id: variant_id },
      ],
    }),
    getFrequencyRatios: builder.query<
      {
        vaf_paf_ratio: { value: number; label: string; tooltip: string } | null;
        faf_daf_ratio: { value: number; label: string; tooltip: string } | null;
      },
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/frequency_ratios/${variant_id}`,
      }),
    }),

    // ─── Molecular Impact ────────────────────────────────────────────────────

    getVariantMolecularImpactHeader: builder.query<
      MolecularImpactHeaderType,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/molecular_impact/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'MolecularImpactHeader', id: variant_id },
      ],
    }),

    getVariantPredictionTools: builder.query<
      any,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/variant_varianttranscript_prediction_tools/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'VariantPredictions', id: variant_id },
      ],
    }),

    getSpliceSiteAlteration: builder.query<
      SilicoPredictionType | undefined,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/splice_site_alteration/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'SpliceSiteAlteration', id: variant_id },
      ],
    }),

    getVariantProteinDomains: builder.query<
      {
        protein_domains: ProteinDomain[];
        date: string | undefined;
        source_version: string | undefined;
      },
      { variant_id?: number | string }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/protein_domains/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'ProteinDomains', id: variant_id },
      ],
    }),

    // ─── Clinical Observation ──────────────────────────────────────────────────

    getSamples: builder.query<
      { results: any[]; total_result_count: number },
      {
        limit: number;
        offset: number;
        search?: string;
        subject_id?: number;
        sortField?: string;
        sortOrder?: 'ascend' | 'descend';
        sample_id_filter?: string[];
        subject_filter?: string[];
        indication_filter?: string[];
        reporter_filter?: string[];
        linked_variants_filter?: string[];
        entry_date_filter?: string[];
        entry_date_from?: string;
        entry_date_to?: string;
      }
    >({
      query: ({
        limit,
        offset,
        search,
        subject_id,
        sortField,
        sortOrder,
        sample_id_filter,
        subject_filter,
        indication_filter,
        reporter_filter,
        linked_variants_filter,
        entry_date_filter,
        entry_date_from,
        entry_date_to,
      }) => ({
        url: '/resultdata/samples',
        params: {
          limit,
          offset,
          search,
          subject_id,
          sortField,
          sortOrder,
          sample_id_filter,
          subject_filter,
          indication_filter,
          reporter_filter,
          linked_variants_filter,
          entry_date_filter,
          entry_date_from,
          entry_date_to,
        },
      }),
      providesTags: ['Samples'],
    }),

    getSampleDetail: builder.query<
      SampleDetail,
      { sample_id?: string | number }
    >({
      query: ({ sample_id }) => ({
        url: `/resultdata/samples/${sample_id}`,
      }),
      providesTags: (result, error, { sample_id }) => [
        { type: 'Samples', id: sample_id },
      ],
    }),

    updateSampleDetail: builder.mutation<
      { id: number },
      {
        sample_id: number | string;
        requestData: {
          indication: string | null;
          comment: string | null;
        };
      }
    >({
      query: ({ sample_id, requestData }) => ({
        url: `/resultdata/samples/${sample_id}`,
        method: 'PUT',
        data: requestData,
      }),
      invalidatesTags: (result, error, { sample_id }) => [
        'Samples',
        { type: 'Samples', id: sample_id },
      ],
    }),

    getWorklist: builder.query<
      { results: WorklistSample[]; total_result_count: number },
      {
        view: 'mine' | 'pool';
        limit: number;
        offset: number;
        search?: string;
        sortField?: string;
        sortOrder?: 'ascend' | 'descend';
        sample_id_filter?: string[];
        subject_filter?: string[];
        indication_filter?: string[];
        reporter_filter?: string[];
        status_filter?: string[];
      }
    >({
      query: ({
        view,
        limit,
        offset,
        search,
        sortField,
        sortOrder,
        sample_id_filter,
        subject_filter,
        indication_filter,
        reporter_filter,
        status_filter,
      }) => ({
        url: '/resultdata/worklist',
        params: {
          view,
          limit,
          offset,
          search,
          sortField,
          sortOrder,
          sample_id_filter,
          subject_filter,
          indication_filter,
          reporter_filter,
          status_filter,
        },
      }),
      providesTags: ['Worklist'],
    }),

    getWorklistFilterOptions: builder.query<Record<string, string[]>, void>({
      query: () => ({
        url: '/resultdata/worklist/filter-options',
      }),
      providesTags: ['SampleFilterOptions'],
    }),

    claimWorklistSample: builder.mutation<
      WorklistSample,
      { sample_id: number }
    >({
      query: ({ sample_id }) => ({
        url: `/resultdata/worklist/${sample_id}/claim`,
        method: 'POST',
      }),
      invalidatesTags: ['Worklist', 'Samples'],
    }),

    getSampleFilterOptions: builder.query<Record<string, string[]>, void>({
      query: () => ({
        url: '/resultdata/samples/filter-options',
      }),
      providesTags: ['SampleFilterOptions'],
    }),

    getVariantSamples: builder.query<
      VariantSample[],
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/samples/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'VariantSamples', id: variant_id },
      ],
    }),

    getVariantDatabases: builder.query<
      VariantDatabase[],
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/databases/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'VariantDatabases', id: variant_id },
      ],
    }),

    getVariantClinvarRecord: builder.query<
      ClinvarRecordType,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/clinvarrecord/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'ClinvarRecord', id: variant_id },
      ],
    }),

    getVariantClinvarAssertions: builder.query<
      ClinvarAssertion[],
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/clinvarassertions/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'ClinvarAssertions', id: variant_id },
      ],
    }),

    getMedicalInvestigationsForVariant: builder.query<
      any,
      { variant_id: number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/medicalinvestigations/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'MedicalInvestigations', id: variant_id },
      ],
    }),

    // ─── References & Linkouts ─────────────────────────────────────────────────

    getVariantReferences: builder.query<
      Reference[],
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/publications/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'VariantReferences', id: variant_id },
      ],
    }),

    getVariantLinkouts: builder.query<
      VariantLinkouts,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/${variant_id}/linkouts`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'VariantLinkouts', id: variant_id },
      ],
    }),

    // ─── Transcripts ───────────────────────────────────────────────────────────

    getVariantTranscripts: builder.query<
      VariantTranscript[],
      { variant_id?: number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/transcriptdata/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'VariantTranscripts', id: variant_id },
      ],
    }),

    // ─── ClinVar Hotspots ─────────────────────────────────────────────────────
    getClinVarHotspotData: builder.query<
      ClinVarHotSpot,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/${variant_id}/clinvar_hotspot`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'ClinVarHotSpot', id: variant_id },
      ],
    }),

    getClinVarHotspotVariants: builder.query<
      ClinVarHotSpotVariantList,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/${variant_id}/clinvar_hotspot/variants`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'ClinVarHotSpotVariantList', id: variant_id },
      ],
    }),

    getClinVarGeneDistribution: builder.query<
      ClinVarGeneDistribution,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/${variant_id}/clinvar_gene_distribution`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'ClinVarGeneDistribution', id: variant_id },
      ],
    }),

    // ─── Other Variants ───────────────────────────────────────────────────────

    // The API returns: { variants, failure_message }.
    getOtherVariants: builder.query<
      OtherVariantsResponse,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/other_variants/${variant_id}`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'OtherVariants', id: variant_id },
      ],
    }),

    // ─── Gene Facts ────────────────────────────────────────────────────────────

    getGeneFactsOverview: builder.query<
      GeneFactsOverview,
      { variant_id?: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/${variant_id}/gene_facts_overview`,
      }),
      providesTags: (result, error, { variant_id }) => [
        { type: 'GeneFactsOverview', id: variant_id },
      ],
    }),

    // Gene operations
    getGeneById: builder.query<Gene, { gene_id: number; langcode?: string }>({
      query: ({ gene_id, langcode = 'de' }) => ({
        url: `/variantdata/gene/${gene_id}?langcode=${langcode}`,
      }),
      providesTags: (result, error, { gene_id }) => [
        { type: 'Gene', id: gene_id },
      ],
    }),

    updateGene: builder.mutation<
      Gene,
      { gene_id: number; requestData: UpdateGeneRequest; langcode?: string }
    >({
      query: ({ gene_id, requestData, langcode = 'de' }) => ({
        url: `/variantdata/gene/${gene_id}?langcode=${langcode}`,
        method: 'PUT',
        data: requestData,
      }),
      invalidatesTags: (result, error, { gene_id }) => [
        { type: 'Gene', id: gene_id },
        // The Gene facts overview table (phenotypes, protein, function) is
        // derived from gene data. Invalidate all GeneFactsOverview entries so
        // the table refetches and reflects the edit immediately.
        { type: 'GeneFactsOverview' },
      ],
    }),

    // PubMed search
    searchPubMedId: builder.mutation<
      any,
      { requestData: { pubmed_id: string } }
    >({
      query: ({ requestData }) => ({
        url: `/variantdata/publication`,
        method: 'POST',
        data: requestData,
      }),
    }),

    // Segregation mutation
    updateSampleSegregation: builder.mutation<
      any,
      {
        variant_id: string | number;
        sample_id: number;
        requestData: { segregation: string | null };
      }
    >({
      query: ({ variant_id, sample_id, requestData }) => ({
        url: `/variantdata/variant/update-segregation/${variant_id}/${sample_id}`,
        method: 'PUT',
        data: requestData,
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'VariantSamples', id: variant_id },
        { type: 'VariantStages', id: variant_id },
      ],
    }),

    // Silico update mutations
    updateSilicoMainData: builder.mutation<
      any,
      { variant_id: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/update_vep_data/${variant_id}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'VariantPredictions', id: variant_id },
        { type: 'SpliceSiteAlteration', id: variant_id },
        { type: 'VariantStages', id: variant_id },
      ],
    }),

    updateSilicoSpliceSiteAlteration: builder.mutation<
      any,
      { variant_id: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/update_spliceai_prediction/${variant_id}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'SpliceSiteAlteration', id: variant_id },
        { type: 'VariantStages', id: variant_id },
      ],
    }),

    // Population allele frequency update
    updatePopulationAlleleFrequency: builder.mutation<
      any,
      { variant_id: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/frequencies/update/${variant_id}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'VariantFrequencies', id: variant_id },
      ],
    }),

    // Protein domains update (original endpoint: match_protein_domains)
    updateVariantProteinDomains: builder.mutation<
      any,
      { variant_id: string | number }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/match_protein_domains/${variant_id}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'ProteinDomains', id: variant_id },
      ],
    }),

    // Evaluation endpoints
    getEvaluation: builder.query<any, { id: number }>({
      query: ({ id }) => ({
        url: `/variantdata/evaluation/${id}`,
      }),
      providesTags: (result, error, { id }) => [{ type: 'Evaluation', id }],
    }),

    getEvaluationValues: builder.query<any, void>({
      query: () => ({
        url: `/variantdata/evaluation-values`,
      }),
      providesTags: ['EvaluationValues'],
    }),

    // ─── Report Generation ─────────────────────────────────────────────────────

    // Step 1 of the two-step molgen report flow: prepare the report on the server.
    // Must be called before deliverMolgenReport / createReportForSample for molgen.
    prepareMolgenReport: builder.mutation<
      void,
      { sample_id: number; langcode?: string; medicalinvestigation_id?: number }
    >({
      query: ({ sample_id, langcode = 'de', medicalinvestigation_id }) => ({
        url: `/resultdata/sample/${sample_id}/prepare_report/`,
        method: 'POST',
        data: { report_type: 'molgen', langcode, medicalinvestigation_id },
      }),
    }),

    // Download a sample report as a binary blob.
    // For the molgen type call prepareMolgenReport first (two-step flow).
    // For pharma the legacy single-step endpoint is used directly.
    // axiosBaseQuery forwards responseType: 'blob' to Axios, which returns
    // the raw binary data so it can be handed to the file-download utility.
    createReportForSample: builder.mutation<
      Blob,
      {
        type: string;
        sample_id: number;
        langcode?: string;
        medicalinvestigation_id?: number;
      }
    >({
      query: ({
        type,
        sample_id,
        langcode = 'de',
        medicalinvestigation_id,
      }) => {
        // Molgen uses the deliver endpoint; all other types use the legacy create endpoint
        const url =
          type.toLowerCase() === 'molgen'
            ? `/resultdata/sample/${sample_id}/deliver_report/`
            : `/resultdata/sample/${sample_id}/create_report/`;
        return {
          url,
          method: 'POST',
          data: { report_type: type, langcode, medicalinvestigation_id },
          // Tell Axios to return the raw binary so callers receive a proper Blob
          responseType: 'blob',
        };
      },
    }),

    // ─── Mutations (Write Operations) ─────────────────────────────────────────────

    // Sample mutations
    addNewSample: builder.mutation<
      any,
      { variant_id: number; requestData: any }
    >({
      query: ({ variant_id, requestData }) => ({
        url: `/variantdata/variant/add-sample/${variant_id}`,
        method: 'POST',
        data: requestData,
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'VariantSamples', id: variant_id },
        { type: 'VariantStages', id: variant_id },
        { type: 'VariantHistory', id: variant_id },
      ],
    }),

    updateSample: builder.mutation<
      any,
      { sample_id: string; requestData: any; variant_id: number | string }
    >({
      query: ({ sample_id, requestData }) => ({
        url: `/variantdata/variant/update-sample-subject/${sample_id}`,
        method: 'PUT',
        data: requestData,
      }),
      invalidatesTags: (result, error, arg) => {
        const variant_id = arg.variant_id;
        return variant_id
          ? [
              { type: 'VariantSamples', id: variant_id },
              { type: 'VariantStages', id: variant_id },
            ]
          : [];
      },
    }),

    unlinkSample: builder.mutation<
      any,
      { sample_id: string; genotype_id: string; variant_id: number | string }
    >({
      query: ({ sample_id, genotype_id }) => ({
        url: `/variantdata/variant/unlink-sample/${sample_id}`,
        method: 'PUT',
        data: { genotype_id },
      }),
      invalidatesTags: (result, error, arg) => {
        const variant_id = arg.variant_id;
        return variant_id
          ? [
              { type: 'VariantSamples', id: variant_id },
              { type: 'VariantStages', id: variant_id },
            ]
          : [];
      },
    }),

    // Variant database mutations
    updateVariantDatabase: builder.mutation<
      any,
      { variant_id: number; requestData: any }
    >({
      query: ({ variant_id, requestData }) => ({
        url: `/variantdata/variant/externaldatabase/${variant_id}`,
        method: 'POST',
        data: requestData,
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'VariantDatabases', id: variant_id },
        { type: 'VariantStages', id: variant_id },
      ],
    }),

    // Primary transcript mutation
    updatePrimaryTranscript: builder.mutation<
      any,
      { variant_id: number; requestData: any }
    >({
      query: ({ variant_id, requestData }) => ({
        url: `/variantdata/variant/update_primary_transcript/${variant_id}`,
        method: 'PUT',
        data: requestData,
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'VariantTranscripts', id: variant_id },
        { type: 'VariantStages', id: variant_id },
        { type: 'VariantComments', id: variant_id },
        { type: 'InterpretationZone', id: variant_id },
        // BIOFRO-80 ABS review: changing primary transcript affects the HGVS
        // nomenclature shown in the variant detail header
        { type: 'VariantDetail', id: variant_id },
        // DNAWZ-625: the new primary transcript's `consequence` is what the
        // Molecular Impact header's "calculated consequence" reads
        // (get_molecular_impact_header reads variant_transcript.consequence),
        // so that cache entry is stale too until this tag invalidates it.
        { type: 'MolecularImpactHeader', id: variant_id },
      ],
    }),

    // Evaluation mutation.
    // variant_id is passed as an arg so invalidatesTags can derive the cache key
    // from the arg rather than the result, which may be undefined on error
    updateEvaluation: builder.mutation<
      any,
      { id: number; requestData: any; variant_id: number | string }
    >({
      query: ({ id, requestData }) => ({
        url: `/variantdata/evaluation/${id}`,
        method: 'PUT',
        data: requestData,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'InterpretationZone', id: arg.variant_id },
        { type: 'VariantStages', id: arg.variant_id },
        { type: 'VariantComments', id: arg.variant_id },
        { type: 'VariantHistory', id: arg.variant_id },
      ],
    }),

    // Variant-assessment stage mutation
    // BIOFRO-80 ABS review: pass variant_id so invalidatesTags can target
    // the specific cache key instead of wiping all VariantStages / VariantArguments.
    updateVariantAssessmentStage: builder.mutation<
      VariantAssessmentStageUpdateResponse,
      {
        id: number;
        status: string;
        variant_id: number | string;
        capture_snapshot_in_frontend?: boolean;
      }
    >({
      query: ({ id, status, capture_snapshot_in_frontend }) => ({
        url: `/variantdata/variantassessmentstage/${id}`,
        method: 'PUT',
        data: { status, capture_snapshot_in_frontend },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'VariantStages', id: arg.variant_id },
        { type: 'VariantArguments', id: arg.variant_id },
        { type: 'InterpretationSnapshots', id: arg.variant_id },
        { type: 'VariantHistory', id: arg.variant_id },
      ],
    }),

    // Prediction tool mutations
    addVariantPredictionTool: builder.mutation<
      any,
      { variant_id: number; requestData: any }
    >({
      query: ({ variant_id, requestData }) => ({
        url: `/variantdata/variant/add_prediction_tools/${variant_id}`,
        method: 'PUT',
        data: requestData,
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'VariantPredictions', id: variant_id },
        { type: 'VariantStages', id: variant_id },
      ],
    }),

    updateVariantPredictionTool: builder.mutation<
      any,
      { tool_id: number; requestData: any; variant_id: number | string }
    >({
      query: ({ tool_id, requestData }) => ({
        url: `/variantdata/variant/edit_variant_varianttranscript_prediction_tool/${tool_id}`,
        method: 'PUT',
        data: requestData,
      }),
      // Use arg.variant_id instead of result?.variant_id so that tags are
      // always invalidated, even when result is undefined (e.g. on error)
      invalidatesTags: (result, error, arg) => [
        { type: 'VariantPredictions', id: arg.variant_id },
        { type: 'VariantStages', id: arg.variant_id },
      ],
    }),

    editSpliceAiPredictionTool: builder.mutation<
      any,
      { tool_id: number; requestData: any; variant_id: number | string }
    >({
      query: ({ tool_id, requestData }) => ({
        url: `/variantdata/variant/spliceai/${tool_id}`,
        method: 'PUT',
        data: requestData,
      }),
      // Use arg.variant_id instead of result?.variant_id (same reason as above)
      invalidatesTags: (result, error, arg) => [
        { type: 'VariantPredictions', id: arg.variant_id },
        { type: 'SpliceSiteAlteration', id: arg.variant_id },
        { type: 'VariantStages', id: arg.variant_id },
      ],
    }),

    // Max AF scoring mutation
    updateMaxcAFScoring: builder.mutation<
      any,
      {
        maxcafscoring_id: number;
        variant_id: number;
        requestData: Record<string, any>;
      }
    >({
      query: ({ maxcafscoring_id, requestData }) => ({
        url: `/variantdata/maxcafscoring/${maxcafscoring_id}`,
        method: 'PUT',
        data: requestData,
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'MaxAFScorings', id: variant_id },
        { type: 'VariantStages', id: variant_id },
      ],
    }),

    // Report variant mutation
    updateReportVariant: builder.mutation<
      any,
      {
        sample_id: string;
        genotype_id: string;
        requestData: any;
        variant_id: number | string;
      }
    >({
      query: ({ sample_id, genotype_id, requestData }) => ({
        url: `/variantdata/reportvariantcategory/update/${sample_id}/${genotype_id}`,
        method: 'POST',
        data: requestData,
      }),
      invalidatesTags: (result, error, arg) => {
        const variant_id = arg.variant_id;
        return variant_id
          ? [
              { type: 'VariantSamples', id: variant_id },
              { type: 'MedicalInvestigations', id: variant_id },
              { type: 'VariantStages', id: variant_id },
              'Samples',
              { type: 'Samples', id: arg.sample_id },
            ]
          : [];
      },
    }),

    // Comment mutations
    // BIOFRO-80 ABS review: pick ONE cache update pattern.  We keep the
    // optimistic update in onQueryStarted and drop invalidatesTags so the
    // comment list does NOT refetch after every edit.
    updateVariantComment: builder.mutation<
      any,
      { comment_id: number; text: string; variant_id?: number | string }
    >({
      query: ({ comment_id, text }) => ({
        url: `/variantdata/edit_comment/${comment_id}`,
        method: 'PUT',
        data: { text },
      }),
      async onQueryStarted(
        { comment_id, text, variant_id },
        { dispatch, queryFulfilled },
      ) {
        // Optimistic update: update the comment in the cache immediately
        if (variant_id) {
          const patchResult = dispatch(
            variantApi.util.updateQueryData(
              'getVariantComments',
              { variant_id },
              (draft) => {
                const comment = draft.find((c: any) => c.id === comment_id);
                if (comment) {
                  comment.notes = text;
                  comment.last_edited = new Date().toISOString().slice(0, 10);
                }
              },
            ),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        }
      },
    }),

    // BIOFRO-80 ABS review: pick ONE cache update pattern.  We keep the
    // optimistic update in onQueryStarted and drop invalidatesTags so the
    // comment list does NOT refetch after every deletion.
    deleteVariantComment: builder.mutation<
      any,
      { comment_id: number; variant_id?: number | string }
    >({
      query: ({ comment_id }) => ({
        url: `/variantdata/delete_comment/${comment_id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(
        { comment_id, variant_id },
        { dispatch, queryFulfilled },
      ) {
        // Optimistic update: patch the cache to remove the comment immediately,
        // then undo the patch if the request fails
        if (variant_id) {
          const patchResult = dispatch(
            variantApi.util.updateQueryData(
              'getVariantComments',
              { variant_id },
              (draft) => {
                const index = draft.findIndex((c: any) => c.id === comment_id);
                if (index !== -1) {
                  draft.splice(index, 1);
                }
              },
            ),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        }
      },
    }),

    // ─── Variant Lock ──────────────────────────────────────────────────────────
    // The lock mutations have no providesTags/invalidatesTags because lock state
    // is tracked as client-side UI state in the variant-workflow slice via
    // matchFulfilled/matchRejected (see RTK Query tutorial Part 8 pattern).

    acquireVariantLock: builder.mutation<
      VariantLock,
      { variant_id: number | string }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/${variant_id}/lock`,
        method: 'POST',
      }),
      // Transform 423 errors so that action.payload has the same { locked_by, message }
      // shape the old createAsyncThunk used – this keeps slice.ts / useVariantLock.ts unchanged.
      // Alqama review: backend now returns locked_by as a structured field
      // (VariantLockErrorSchema.locked_by) so we avoid fragile regex parsing.
      transformErrorResponse: (response: AxiosBaseQueryError) => {
        if (response.status === 423) {
          const data = response.data as any;
          return {
            locked_by: data?.locked_by || 'unknown',
            message: data?.detail || '',
            lock_type: data?.lock_type,
            running_task_id: data?.running_task_id,
          };
        }
        return response;
      },
    }),

    refreshVariantLock: builder.mutation<
      VariantLock,
      { variant_id: number | string }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/${variant_id}/lock`,
        method: 'PATCH',
      }),
    }),

    releaseVariantLock: builder.mutation<void, { variant_id: number | string }>(
      {
        query: ({ variant_id }) => ({
          url: `/variantdata/variant/${variant_id}/lock`,
          method: 'DELETE',
        }),
      },
    ),

    // ─── Auto-Recalculation ────────────────────────────────────────────────────

    recalculateAutoAssessmentArguments: builder.mutation<
      any,
      { variant_id: string | number; stage_type: string }
    >({
      query: ({ variant_id, stage_type }) => ({
        url: `/variantdata/variant/${variant_id}/recalculate_auto_assessment_arguments/${stage_type}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'VariantArguments', id: variant_id },
        { type: 'VariantStages', id: variant_id },
        { type: 'InterpretationZone', id: variant_id },
        { type: 'VariantHistory', id: variant_id },
      ],
    }),

    refreshAndRecalculateVariantData: builder.mutation<
      RefreshAndRecalculateResponse,
      {
        variant_id: string | number;
        capture_snapshot_in_frontend?: boolean;
      }
    >({
      query: ({ variant_id, capture_snapshot_in_frontend }) => ({
        url: `/variantdata/variant/${variant_id}/refresh_and_recalculate`,
        method: 'POST',
        data: { capture_snapshot_in_frontend },
      }),
      // Invalidate the search table too, so the "recalculating" badge shows up
      // immediately once the user is redirected to the variant search page.
      invalidatesTags: ['RunningTasks', 'Variants'],
    }),

    // ─── Frequency Data Mutations ──────────────────────────────────────────────

    setAsDefaultMaxCAFScoring: builder.mutation<
      any,
      { id: number; variant_id: number | string }
    >({
      query: ({ id }) => ({
        url: `/variantdata/maxcafscoring/${id}/set_as_default`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'MaxAFScorings', id: variant_id },
        { type: 'VariantStages', id: variant_id },
        { type: 'VariantFrequencies', id: variant_id },
      ],
    }),

    updateGeneTopVariantsThreshold: builder.mutation<
      any,
      {
        gene_id: number;
        allele_frequency: string;
        overriden_max_af_clinvar_id: string;
        variant_id?: number | string;
      }
    >({
      query: ({ gene_id, allele_frequency, overriden_max_af_clinvar_id }) => ({
        url: `/variantdata/gene/${gene_id}/update_gene_top_variants_threshold`,
        method: 'PUT',
        data: { allele_frequency, overriden_max_af_clinvar_id },
      }),
      invalidatesTags: (result, error, { gene_id, variant_id }) => [
        { type: 'GeneTopPatVarFrequencies', id: gene_id },
        ...(variant_id
          ? [{ type: 'VariantStages' as const, id: variant_id }]
          : []),
      ],
    }),

    // ─── Assessment Confirmation ───────────────────────────────────────────────

    createVariantAssessmentConfirmation: builder.mutation<
      any,
      { variant_id: number | string }
    >({
      query: ({ variant_id }) => ({
        url: `/variantdata/variant/confirm/${variant_id}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'VariantHistory', id: variant_id },
        { type: 'VariantStages', id: variant_id },
      ],
    }),

    // ─── Clinical Interpretation ───────────────────────────────────────────────

    updateClinicalInterpretationByPostP: builder.mutation<
      any,
      {
        variant_id: number | string;
        requestData: { clinical_interpretation: string };
      }
    >({
      query: ({ variant_id, requestData }) => ({
        url: `/variantdata/variant/interpretation_zone/${variant_id}/set_by_post_p`,
        method: 'PUT',
        data: requestData,
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'InterpretationZone', id: variant_id },
        { type: 'VariantStages', id: variant_id },
        { type: 'VariantHistory', id: variant_id },
      ],
    }),

    createInterpretationSnapshot: builder.mutation<
      InterpretationSnapshot,
      {
        variant_id: string | number;
        requestData: InterpretationSnapshotCreateRequest;
      }
    >({
      query: ({ variant_id, requestData }) => ({
        url: `/variantdata/variant/${variant_id}/interpretation-snapshots`,
        method: 'POST',
        data: requestData,
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'InterpretationSnapshots', id: variant_id },
      ],
    }),

    // ─── ClinVar Record Update ─────────────────────────────────────────────────
    // Returns either { status: 'AMBIGUOUS', choices: [...] } or the updated record.
    // invalidatesTags only fires when the update was non-ambiguous.

    fetchUpdatedClinvarRecord: builder.mutation<
      ClinvarRecordType | ClinVarAmbiguousResponse,
      { variant_id: string | number; force_clinvar_id?: string }
    >({
      query: ({ variant_id, force_clinvar_id }) => ({
        url: `/variantdata/variant/update_clinvar_record/${variant_id}`,
        method: 'PUT',
        params: force_clinvar_id ? { force_clinvar_id } : undefined,
      }),
      invalidatesTags: (result, error, { variant_id }) => {
        // Only invalidate when not AMBIGUOUS
        if (result && 'status' in result && result.status === 'AMBIGUOUS') {
          return [];
        }
        return [
          { type: 'ClinvarRecord', id: variant_id },
          { type: 'ClinvarAssertions', id: variant_id },
        ];
      },
    }),

    // ─── Report Category ──────────────────────────────────────────────────────

    updateVariantReportCategory: builder.mutation<
      any,
      {
        reportvariantcategory_id: number;
        requestData: { report_category: string };
        sample_id?: number | string;
        variant_id?: number | string;
      }
    >({
      query: ({ reportvariantcategory_id, requestData }) => ({
        url: `/variantdata/reportvariantcategory/${reportvariantcategory_id}`,
        method: 'PUT',
        data: requestData,
      }),
      invalidatesTags: (result, error, { sample_id, variant_id }) => [
        ...(variant_id
          ? [
              { type: 'VariantSamples' as const, id: variant_id },
              { type: 'MedicalInvestigations' as const, id: variant_id },
              { type: 'VariantStages' as const, id: variant_id },
            ]
          : []),
        ...(sample_id
          ? ['Samples' as const, { type: 'Samples' as const, id: sample_id }]
          : []),
      ],
    }),

    // ─── Variant Arguments ────────────────────────────────────────────────────

    updateVariantAssessmentArguments: builder.mutation<
      any,
      {
        variant_id: string | number;
        requestData: {
          argumentdata: {
            argument_id: number;
            comment?: string;
            is_argument_met?: boolean;
          }[];
          stage?: string;
        };
      }
    >({
      query: ({ variant_id, requestData }) => ({
        url: `/variantdata/variant/${variant_id}/add_assessment_arguments`,
        method: 'POST',
        data: requestData,
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'VariantArguments', id: variant_id },
        { type: 'VariantStages', id: variant_id },
        { type: 'InterpretationZone', id: variant_id },
        { type: 'VariantHistory', id: variant_id },
      ],
    }),

    // ─── Publications ─────────────────────────────────────────────────────────

    addPublicationToVariant: builder.mutation<
      any,
      {
        variant_id: string | number;
        requestData: {
          publication_id: number;
          type: string;
          variant_included: boolean;
          include_in_report: boolean;
          comment_materialmethod: string;
          comment_result: string;
          comment_evidence: string;
          comment_conclusion: string;
        };
      }
    >({
      query: ({ variant_id, requestData }) => ({
        url: `/variantdata/variant/${variant_id}/addpublication`,
        method: 'POST',
        data: requestData,
      }),
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'VariantReferences', id: variant_id },
      ],
    }),

    updatePublicationToVariant: builder.mutation<
      any,
      {
        variant_publication_id: string | number;
        requestData: {
          publication_id: number;
          type: string;
          variant_included: boolean;
          include_in_report: boolean;
          comment_materialmethod: string;
          comment_result: string;
          comment_evidence: string;
          comment_conclusion: string;
        };
        variant_id?: string | number;
      }
    >({
      query: ({ variant_publication_id, requestData }) => ({
        url: `/variantdata/variantpublication/${variant_publication_id}`,
        method: 'PUT',
        data: requestData,
      }),
      invalidatesTags: (result, error, { variant_id }) =>
        variant_id ? [{ type: 'VariantReferences', id: variant_id }] : [],
    }),

    // ─── Existing Variant Database (PUT) ──────────────────────────────────────
    // Note: updateVariantDatabase (above) does a POST (create new entry).
    // updateExistingVariantDatabase does a PUT (update existing entry by its own ID).

    updateExistingVariantDatabase: builder.mutation<
      any,
      {
        variant_external_db_id: number;
        requestData: VariantDatabaseUpdateRequest;
        variant_id?: number | string;
      }
    >({
      query: ({ variant_external_db_id, requestData }) => ({
        url: `/variantdata/variant/database/${variant_external_db_id}`,
        method: 'PUT',
        data: requestData,
      }),
      invalidatesTags: (result, error, { variant_id }) =>
        variant_id
          ? [
              { type: 'VariantDatabases', id: variant_id },
              { type: 'VariantStages', id: variant_id },
            ]
          : [],
    }),

    // ─── Sample Zygosity ──────────────────────────────────────────────────────

    updateSampleZygosity: builder.mutation<
      any,
      {
        variant_id: string | number;
        sample_id: number;
        requestData: { zygosity: string };
      }
    >({
      query: ({ variant_id, sample_id, requestData }) => ({
        url: `/variantdata/variant/update-zygosity/${variant_id}/${sample_id}`,
        method: 'PUT',
        data: requestData,
      }),
      //
      // Original Redux behaviour: the fulfilled reducer did a selective merge –
      // only zygosity and genotype_id were overwritten; segregation, report_data
      // and comment were left untouched in state.clinical_observation.variant_samples.
      //
      // We replicate that behaviour here with onQueryStarted + updateQueryData,
      // a pessimistic update applied after the request resolves
      async onQueryStarted(
        { variant_id, sample_id },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedSample } = await queryFulfilled;
          dispatch(
            variantApi.util.updateQueryData(
              'getVariantSamples',
              { variant_id },
              (draft) => {
                const sample = draft.find((s) => s.sample_id === sample_id);
                if (sample) {
                  // Selective merge – only fields that truly changed.
                  sample.zygosity = updatedSample.zygosity;
                  if (updatedSample.genotype_id !== undefined) {
                    sample.genotype_id = updatedSample.genotype_id;
                  }
                  // Preserve segregation, report_data, comment, etc.
                }
              },
            ),
          );
        } catch {
          // Pessimistic update: we only patch the cache after queryFulfilled
          // resolves, so on rejection there is nothing to undo
        }
      },
      // Only refresh stages – do NOT invalidate VariantSamples (see onQueryStarted).
      invalidatesTags: (result, error, { variant_id }) => [
        { type: 'VariantStages', id: variant_id },
      ],
    }),
  }),
});

// ─── Auto-generated Hooks ────────────────────────────────────────────────────

// Export the auto-generated React hooks for each query and mutation endpoint.
// The hook names are generated automatically based on the endpoint names:
//   query     -> useGet<Name>Query
//   mutation  -> use<Name>Mutation

export const {
  useGetVariantDetailQuery,
  useGetRunningTasksQuery,
  useUploadVcfMutation,
  useGetVariantCommentsQuery,
  useGetVariantHistoryQuery,
  useGetVariantAssessmentStagesQuery,
  useGetVariantInterpretationZoneQuery,
  useGetVariantAssessmentArgumentsQuery,
  useGetInterpretationSnapshotsQuery,
  useGetInterpretationSnapshotQuery,
  useGetVariantFrequenciesQuery,
  useGetGeneTopPatVarFrequenciesQuery,
  useGetMaxAFScoringsQuery,
  useGetFrequencyRatiosQuery,
  useGetVariantMolecularImpactHeaderQuery,
  useGetVariantPredictionToolsQuery,
  useGetSpliceSiteAlterationQuery,
  useGetVariantProteinDomainsQuery,
  useGetSamplesQuery,
  useGetSampleDetailQuery,
  useUpdateSampleDetailMutation,
  useGetWorklistQuery,
  useGetWorklistFilterOptionsQuery,
  useClaimWorklistSampleMutation,
  useGetSampleFilterOptionsQuery,
  useGetVariantSamplesQuery,
  useGetVariantDatabasesQuery,
  useGetVariantClinvarRecordQuery,
  useGetVariantClinvarAssertionsQuery,
  useGetMedicalInvestigationsForVariantQuery,
  useGetVariantReferencesQuery,
  useGetVariantLinkoutsQuery,
  useGetClinVarHotspotDataQuery,
  useGetClinVarHotspotVariantsQuery,
  useGetClinVarGeneDistributionQuery,
  useGetVariantTranscriptsQuery,
  useGetOtherVariantsQuery,
  useGetGeneFactsOverviewQuery,
  useGetGeneByIdQuery,
  useGetEvaluationQuery,
  useGetEvaluationValuesQuery,
  // Mutation hooks
  useAddNewSampleMutation,
  useUpdateSampleMutation,
  useUnlinkSampleMutation,
  useUpdateVariantDatabaseMutation,
  useUpdatePrimaryTranscriptMutation,
  useUpdateEvaluationMutation,
  useUpdateVariantAssessmentStageMutation,
  useAddVariantPredictionToolMutation,
  useUpdateVariantPredictionToolMutation,
  useEditSpliceAiPredictionToolMutation,
  useUpdateMaxcAFScoringMutation,
  useUpdateReportVariantMutation,
  useUpdateVariantCommentMutation,
  useDeleteVariantCommentMutation,
  useUpdateGeneMutation,
  useSearchPubMedIdMutation,
  useUpdateSampleSegregationMutation,
  useUpdateSilicoMainDataMutation,
  useUpdateSilicoSpliceSiteAlterationMutation,
  useUpdatePopulationAlleleFrequencyMutation,
  useUpdateVariantProteinDomainsMutation,
  usePrepareMolgenReportMutation,
  useCreateReportForSampleMutation,
  // New migration mutations
  useAcquireVariantLockMutation,
  useRefreshVariantLockMutation,
  useReleaseVariantLockMutation,
  useRecalculateAutoAssessmentArgumentsMutation,
  useRefreshAndRecalculateVariantDataMutation,
  useSetAsDefaultMaxCAFScoringMutation,
  useUpdateGeneTopVariantsThresholdMutation,
  useCreateVariantAssessmentConfirmationMutation,
  useUpdateClinicalInterpretationByPostPMutation,
  useCreateInterpretationSnapshotMutation,
  useFetchUpdatedClinvarRecordMutation,
  useUpdateVariantReportCategoryMutation,
  useUpdateVariantAssessmentArgumentsMutation,
  useAddPublicationToVariantMutation,
  useUpdatePublicationToVariantMutation,
  useUpdateExistingVariantDatabaseMutation,
  useUpdateSampleZygosityMutation,
} = variantApi;

// Compatibility aliases for consumers that still use the pre-assessment hook names.
export const useGetVariantStagesQuery = useGetVariantAssessmentStagesQuery;
export const useGetVariantArgumentsQuery =
  useGetVariantAssessmentArgumentsQuery;
export const useUpdateVariantStageMutation =
  useUpdateVariantAssessmentStageMutation;
export const useUpdateVariantArgumentsMutation =
  useUpdateVariantAssessmentArgumentsMutation;
export const useRecalculateAutoArgumentsMutation =
  useRecalculateAutoAssessmentArgumentsMutation;
